import type { Translation } from "../../../hooks/useTranslations";
import {
  getSecondaryItemQuantityRule,
  isAluminiumSubstructureMaterial,
  type DeckDesignerResult,
} from "../../../http/deckDesigner/formulas";
import type { ReportValue } from "../../../http/deckDesigner/types/report";
import type { Item } from "../../../http/types/items";
import type { BillRow } from "./billOfQuantities.types";
import type { MeasurementFormat, MetricLengthUnit } from "./measurementFormat";
import { EMPTY_VALUE, formatQuantity, isFiniteNumber } from "../../../utils/numberFormat";
import { toOrderQuantity } from "../../../utils/orderQuantity";
import type { ReportValues } from "./reportValues";

// A bill-of-quantities row with what the PDF report needs of it too. productId: what the report
// identifies the product by - its item id, the substructure's own id for a substructure.
export type ProductBillRow = BillRow & { productId: string; quantityValue: ReportValue | null };

// Quantity to order: rounded up to a multiple of the item's order multiple (pieces per pack, synced from
// Snowflake). 1 - legacy items - or missing (no item either) leaves it unchanged.
export const toOrder = (quantity: number | null | undefined, item: Item | undefined): number | null | undefined => {
  if (!isFiniteNumber(quantity)) {
    return quantity;
  }

  return toOrderQuantity(quantity, item?.minOrderQuantity);
};

// The bill-of-quantities table, in the UI language, one row per selected product.
export const buildBillOfQuantitiesRows = (
  result: DeckDesignerResult,
  translation: Translation,
  measurementFormat: MeasurementFormat,
  reportValues: ReportValues,
): ProductBillRow[] => {
  const languageCode = translation.languageCode;
  const common = translation.common;
  const calculation = translation.deckDesignerModule.configurations.calculation;
  const { lengthValue, piecesValue } = reportValues;
  const fmtQuantity = (value: number | null | undefined, unit: string, decimals = 0): string =>
    formatQuantity(languageCode, value, unit, decimals);
  // Lengths follow the unit of measure; metric reads exactly as fmtQuantity would.
  const lengthUnitTexts: Record<MetricLengthUnit, string> = { mm: common.unitMm, cm: common.unitCm, m: common.unitM };
  const fmtLength = (value: number | null | undefined, unit: MetricLengthUnit, decimals = 0): string =>
    isFiniteNumber(value)
      ? measurementFormat.length(value, unit, fmtQuantity(value, lengthUnitTexts[unit], decimals))
      : EMPTY_VALUE;

  const secondaryItemQuantityRule = getSecondaryItemQuantityRule(result.secondaryItem?.item.code);
  const rows: ProductBillRow[] = [];

  if (result.support) {
    rows.push({
      label: translation.deckDesignerModule.supports.plural,
      kind: "supports",
      productId: result.support.item.id,
      productName: result.support.supportFamily.name,
      description: result.support.item.description ?? EMPTY_VALUE,
      code: result.support.item.code,
      requiredQuantity: fmtQuantity(result.quantities.supports, common.unitPcs),
      quantity: fmtQuantity(toOrder(result.quantities.supports, result.support.item), common.unitPcs),
      quantityValue: piecesValue(toOrder(result.quantities.supports, result.support.item)),
    });
  }

  if (result.supportScrew) {
    rows.push({
      label: calculation.supportScrew,
      kind: "support_screw",
      productId: result.supportScrew.item.id,
      productName: result.supportScrew.screwFamily.name,
      description: result.supportScrew.item.description ?? EMPTY_VALUE,
      code: result.supportScrew.item.code,
      requiredQuantity: fmtQuantity(result.quantities.supportScrews, common.unitPcs),
      quantity: fmtQuantity(toOrder(result.quantities.supportScrews, result.supportScrew.item), common.unitPcs),
      quantityValue: piecesValue(toOrder(result.quantities.supportScrews, result.supportScrew.item)),
    });
  }

  if (result.substructure && result.substructure.substructureMaterials.some(isAluminiumSubstructureMaterial)) {
    rows.push({
      label: translation.deckDesignerModule.substructures.singular,
      kind: "substructure",
      productId: result.substructure.id,
      productName: result.substructure.substructureFamily?.name ?? result.substructure.item?.description ?? EMPTY_VALUE,
      description: result.substructure.item?.description ?? EMPTY_VALUE,
      code: result.substructure.item?.code ?? EMPTY_VALUE,
      requiredQuantity: fmtLength(result.quantities.substructureLength, "m", 2),
      quantity: fmtLength(result.quantities.substructureLength, "m", 2),
      quantityValue: lengthValue(result.quantities.substructureLength, "m", 2),
    });
  }

  if (result.connector) {
    rows.push({
      label: translation.deckDesignerModule.connectors.singular,
      kind: "connector",
      productId: result.connector.item.id,
      productName: result.connector.connectorFamily.name,
      description: result.connector.item.description ?? EMPTY_VALUE,
      code: result.connector.item.code,
      requiredQuantity: fmtQuantity(result.connectorQuantity, common.unitPcs),
      quantity: fmtQuantity(toOrder(result.quantities.connectors, result.connector.item), common.unitPcs),
      quantityValue: piecesValue(toOrder(result.quantities.connectors, result.connector.item)),
    });
  }

  if (
    result.secondaryItem &&
    secondaryItemQuantityRule?.considerIntersections &&
    isFiniteNumber(secondaryItemQuantityRule.joistMultiplier)
  ) {
    rows.push({
      label: result.secondaryItem.item.code,
      kind: "secondary_item",
      productId: result.secondaryItem.item.id,
      productName: result.secondaryItem.item.code,
      description: result.secondaryItem.item.description ?? EMPTY_VALUE,
      code: result.secondaryItem.item.code,
      requiredQuantity: fmtQuantity(result.secondaryItemQuantity, common.unitPcs),
      quantity: fmtQuantity(toOrder(result.quantities.secondaryItems, result.secondaryItem.item), common.unitPcs),
      quantityValue: piecesValue(toOrder(result.quantities.secondaryItems, result.secondaryItem.item)),
    });
  }

  if (result.connectorSubstructureScrew) {
    rows.push({
      label: calculation.connectorSubstructureScrew,
      kind: "connector_substructure_screw",
      productId: result.connectorSubstructureScrew.item.id,
      productName: result.connectorSubstructureScrew.screwFamily.name,
      description: result.connectorSubstructureScrew.item.description ?? EMPTY_VALUE,
      code: result.connectorSubstructureScrew.item.code,
      requiredQuantity: fmtQuantity(result.connectorSubstructureScrewQuantity, common.unitPcs),
      quantity: fmtQuantity(
        toOrder(result.quantities.connectorSubstructureScrews, result.connectorSubstructureScrew.item),
        common.unitPcs,
      ),
      quantityValue: piecesValue(
        toOrder(result.quantities.connectorSubstructureScrews, result.connectorSubstructureScrew.item),
      ),
    });
  }

  if (result.connectorBoardScrew) {
    rows.push({
      label: calculation.connectorBoardScrew,
      kind: "connector_board_screw",
      productId: result.connectorBoardScrew.item.id,
      productName: result.connectorBoardScrew.screwFamily.name,
      description: result.connectorBoardScrew.item.description ?? EMPTY_VALUE,
      code: result.connectorBoardScrew.item.code,
      requiredQuantity: fmtQuantity(result.connectorBoardScrewQuantity, common.unitPcs),
      quantity: fmtQuantity(
        toOrder(result.quantities.connectorBoardScrews, result.connectorBoardScrew.item),
        common.unitPcs,
      ),
      quantityValue: piecesValue(toOrder(result.quantities.connectorBoardScrews, result.connectorBoardScrew.item)),
    });
  }

  return rows;
};
